import {ChangeDetectionStrategy, Component, OnDestroy, OnInit, signal, ViewEncapsulation} from '@angular/core';

import {Subscription} from 'rxjs';
import {LoaderService} from '../../../../services/loader.service';
import {ToolsService} from '../../../../services/tools.service';
import {MetaTag} from '../../../../types/MetaTag';
import {UrlBuilderService} from '../../../../services/url-builder.service';
import {SeoMetaService} from '../../../../services/seo-meta.service';
import {WpService} from '../../../../services/wp.service';
import {WPPageCustom} from '../../../../types/WPPageCustom';
import {StaticPagesEnum} from '../../../../enums/StaticPagesEnums';
import {finalize} from 'rxjs/operators';
import {GuideUrlResolverService} from '../../services/guide-url-resolver.service';
import {ActivatedRoute} from '@angular/router';
import {LoaderQueueItem} from '../../../../types/LoaderQueueItem';
import {DLPageTypeEnum} from '../../../../enums/DataLayerEnums';
import {DataLayerParamsPageViewGA4} from '../../../../types/DataLayerParamsPageViewGA4';
import {PageViewsGa4ResolverService} from '../../../../services/page-views-ga4-resolver.service';
import {AppConfigService} from '../../../../services/app-config.service';
import {SearchReloadService} from '../../../../services/search/search-reload.service';
import {SearchService} from '../../../../services/search/search.service';
import {StaticGuideService} from '../../../../services/static-guide.service';
import {WPGuideAggregated} from '../../../../types/WPGuideAggregated';
import {WPGuideGroup} from '../../../../types/WPGuideGroup';
import {StaticPagesService} from '../../../../services/static-pages.service';

@Component({
  selector: 'app-guide-home',
  templateUrl: './guide-home.component.html',
  styleUrls: ['./guide-home.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GuideHomeComponent implements OnInit, OnDestroy {
  public pageData = signal<WPPageCustom>(null);
  public puffsLimit = 4;
  public guideGroups = signal<WPGuideGroup[]>(null);
  public pageType: DLPageTypeEnum = DLPageTypeEnum.GUIDE_PAGE;
  private pageTitle: string;
  private subscriptions: Subscription[] = [];

  constructor(
    public urlResolver: GuideUrlResolverService,
    private loaderService: LoaderService,
    private tools: ToolsService,
    private urlBuilder: UrlBuilderService,
    private metaService: SeoMetaService,
    private wpService: WpService,
    private route: ActivatedRoute,
    private staticPagesService: StaticPagesService,
    private pageViewGa4Resolver: PageViewsGa4ResolverService,
    private appConfigService: AppConfigService,
    private reloadService: SearchReloadService,
    private searchService: SearchService,
    private guideService: StaticGuideService,
  ) {}

  ngOnInit() {
    this.searchService.softResetState();
    this.initUrlResolverAndUpdateState();
    this.updateGuideAggregationData();
    this.updatePageData();
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private initUrlResolverAndUpdateState(): void {
    this.reloadService.updateParams(this.route.snapshot.params, this.route.snapshot.queryParams);
    this.urlResolver.cleanUpUrl(this.route.snapshot);
    this.urlResolver.resetState();
  }

  private updatePageData(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    this.subscriptions.push(
      this.staticPagesService
        .GetPageBySlug(StaticPagesEnum.GUIDES)
        .pipe(
          finalize(() => {
            this.loaderService.removeFromQueue(queryId);
          }),
        )
        .subscribe(
          (result) => {
            if (result) {
              this.processMainPageResults(result);
            } else {
              console.error('No result for guide main page');
              this.onDataLoadingFinish();
            }
          },
          (error) => {
            console.error(error);
            this.onDataLoadingFinish();
          },
        ),
    );
  }

  private processMainPageResults(result: WPPageCustom): void {
    this.pageData.set(result);
    this.updateMetaTags();
    this.scrollToTop();
    this.onDataLoadingFinish();
  }

  private updateGuideAggregationData(): void {
    const queryId: number = this.loaderService.generateQueueId();
    const queryItem: LoaderQueueItem = {
      id: queryId,
      title: '',
      description: '',
    };
    this.loaderService.addToQueue(queryItem);
    this.subscriptions.push(
      this.guideService
        .GetGuideAggregated()
        .pipe(
          finalize(() => {
            this.loaderService.removeFromQueue(queryId);
          }),
        )
        .subscribe(
          (result) => {
            this.processAggregatedGuide(result);
          },
          (error) => console.error(error),
        ),
    );
  }

  private scrollToTop(): void {
    this.tools.scrollTo();
  }

  private updateMetaTags(): void {
    const metaTags: MetaTag[] = [];
    const page: WPPageCustom = this.pageData();
    const suffix = ' - Vården.se';

    if (!this.tools.isNil(page) && !this.tools.isNil(page.acf)) {
      const title: string = page.acf.seoTitle;
      const description: string = page.acf.seoDescription;
      this.pageTitle = title + suffix;

      metaTags.push({
        value: title + suffix,
        type: 'title',
        field: 'title',
      });

      metaTags.push({
        value: title + suffix,
        type: 'property',
        field: 'og:title',
      });

      metaTags.push({
        value: 'website',
        type: 'property',
        field: 'og:type',
      });

      metaTags.push({
        value: this.urlBuilder.CreateStaticLink(this.urlBuilder.CreateGuideRouterLink()),
        type: 'property',
        field: 'og:url',
      });

      metaTags.push({
        value: description,
        type: 'name',
        field: 'description',
      });

      metaTags.push({
        value: description,
        type: 'property',
        field: 'og:description',
      });

      metaTags.push({
        value: this.appConfigService.appConfig.siteName,
        type: 'property',
        field: 'og:site_name',
      });

      this.metaService.updateMetaTags(metaTags);
    }
  }

  private onDataLoadingFinish(): void {
    const customGa4Params: DataLayerParamsPageViewGA4 = {
      pageType: DLPageTypeEnum.GUIDE_PAGE,
      pageTitle: this.pageTitle,
    };
    this.pageViewGa4Resolver.trackPageView(customGa4Params);
  }

  private processAggregatedGuide(result: WPGuideAggregated) {
    const guideGroups: WPGuideGroup[] = [];
    Object.values(result).forEach((item) => {
      if (item.posts.length > 0) {
        guideGroups.push(item);
      }
    });
    this.guideGroups.set(guideGroups);
  }
}
